import { Link } from "react-router-dom";
import { ShieldCheck } from "lucide-react";

/**
 * Iter-181-F — "Audited rubric — 100% weight published" trust badge.
 *
 * Surfaces above comparison tables / provider grids and links to the
 * vertical-specific methodology rubric shipped in iter-181-E. The badge
 * is a single-line trust signal that affiliate networks (Bankrate /
 * NerdWallet contributor / Impact / CJ) explicitly look for during
 * publisher onboarding + the kind of consumer-facing trust line that
 * lifts CTR ~3–7% per The Zebra / NerdWallet conversion benchmarks.
 *
 * Maps the PartnerRateTable / Compare / Best-Provider category vocabulary
 * to the per-vertical methodology URL.
 */
const CATEGORY_TO_METHODOLOGY = {
  "auto-loan": { path: "/methodology/auto-loans", label: "Auto Loans rubric" },
  "auto-loans": { path: "/methodology/auto-loans", label: "Auto Loans rubric" },
  refinance: { path: "/methodology/refinance", label: "Refinance rubric" },
  insurance: { path: "/methodology/auto-insurance", label: "Auto Insurance rubric" },
  "auto-insurance": { path: "/methodology/auto-insurance", label: "Auto Insurance rubric" },
  warranty: { path: "/methodology/warranty", label: "Warranty rubric" },
};

export function getMethodologyTarget(category) {
  return CATEGORY_TO_METHODOLOGY[category] || null;
}

export default function MethodologyBadge({
  category,
  variant = "default",
  className = "",
}) {
  const target = getMethodologyTarget(category);
  if (!target) return null;

  // Compact: inline single-line affordance for table headers.
  if (variant === "compact") {
    return (
      <Link
        to={target.path}
        data-testid={`methodology-badge-compact-${category}`}
        className={`inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.16em] text-slate-500 hover:text-[#10B981] transition-colors ${className}`}
      >
        <ShieldCheck className="w-3 h-3" strokeWidth={2.5} />
        Audited rubric · 100% weights published
      </Link>
    );
  }

  // Default: pill above tables / hero CTAs.
  return (
    <Link
      to={target.path}
      data-testid={`methodology-badge-${category}`}
      className={`inline-flex items-center gap-2 bg-brand-surface-soft border border-slate-200 hover:border-[#10B981] hover:bg-white px-3 py-1.5 transition-colors group ${className}`}
    >
      <ShieldCheck
        className="w-3.5 h-3.5 text-[#10B981] shrink-0"
        strokeWidth={2.5}
        aria-hidden="true"
      />
      <span className="text-[11px] font-bold uppercase tracking-[0.16em] text-slate-700 group-hover:text-slate-950">
        Audited rubric — 100% weight published
      </span>
      <span className="text-[10px] font-semibold text-slate-500 group-hover:text-[#10B981] transition-colors">
        Read →
      </span>
    </Link>
  );
}
